import React, { useEffect, useMemo, useState } from 'react';
import { Copy, KeyRound, Plus, Shield } from 'lucide-react';
import { toast } from 'sonner';

import DataTable from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import StatusBadge from '../components/StatusBadge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { enrollmentApi } from '../lib/api';

function formatTimestamp(value) {
  if (!value) return 'Never';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

export default function Enrollment() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showInstaller, setShowInstaller] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createdToken, setCreatedToken] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [form, setForm] = useState({ max_uses: '', expires_hours: '24' });
  const pageSize = 12;

  const loadTokens = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await enrollmentApi.list({ page, page_size: pageSize });
      setTokens(response.data?.items || []);
      setTotal(response.data?.total || 0);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to load enrollment tokens.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTokens();
  }, [page]);

  const createToken = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await enrollmentApi.create({
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_hours: Number(form.expires_hours),
      });
      setCreatedToken(response.data?.token || '');
      setShowCreate(false);
      toast.success('Enrollment token created. Copy it now; it will not be shown again.');
      loadTokens();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create token.');
    } finally {
      setSaving(false);
    }
  };

  const revokeToken = async (id) => {
    try {
      await enrollmentApi.revoke(id);
      toast.success('Token revoked.');
      loadTokens();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke token.');
    }
  };

  const installerCommands = useMemo(() => {
    const origin = window.location.origin;
    return {
      mac: `curl -fsSL "${origin}/api/v1/enrollment-tokens/install.sh?token=${createdToken || 'TOKEN'}" | bash -s -- EMPLOYEE_ID`,
      windows: `irm "${origin}/api/v1/enrollment-tokens/install.ps1?token=${createdToken || 'TOKEN'}" | iex`,
    };
  }, [createdToken]);

  return (
    <div className="min-h-screen space-y-6 bg-slate-900 p-6 text-slate-300" data-testid="enrollment-page">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Enrollment Tokens</h1>
          <p className="mt-2 text-slate-500">
            Manage device enrollment credentials, revoke stale tokens, and distribute installer commands.
          </p>
        </div>
        <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" />
          Create Token
        </Button>
      </div>

      {createdToken ? (
        <Card className="border-slate-700 bg-slate-800 shadow-lg">
          <CardContent className="space-y-4 p-6">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-amber-400" />
              <div>
                <p className="font-semibold text-white">Token shown once</p>
                <p className="text-sm text-slate-500">Copy and store this value immediately.</p>
              </div>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-900 p-4 font-mono text-sm text-amber-300">
              {createdToken}
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card className="border-slate-700 bg-slate-800 shadow-lg">
        <CardHeader className="pb-0">
          <CardTitle className="text-white">Token Registry</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          {error ? (
            <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-300">
              {error}
            </div>
          ) : null}

          {loading ? (
            <LoadingSkeleton type="table" />
          ) : tokens.length ? (
            <DataTable
              columns={[
                {
                  key: 'token_prefix',
                  label: 'Token',
                  render: (value) => value || 'Hidden',
                },
                {
                  key: 'max_uses',
                  label: 'Device Limit',
                  render: (value) => value || 'Unlimited',
                },
                { key: 'used_count', label: 'Used Count' },
                {
                  key: 'expires_at',
                  label: 'Expires At',
                  render: (value) => formatTimestamp(value),
                },
                {
                  key: 'status',
                  label: 'Status',
                  render: (_, row) => <StatusBadge status={row.is_active ? 'online' : 'offline'} />,
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  render: (_, row) => (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="bg-red-600 text-white hover:bg-red-700"
                        disabled={!row.is_active}
                        onClick={(event) => {
                          event.stopPropagation();
                          revokeToken(row.id);
                        }}
                      >
                        Revoke
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-slate-600 bg-slate-900 text-slate-300 hover:bg-slate-700"
                        onClick={(event) => {
                          event.stopPropagation();
                          setShowInstaller(true);
                        }}
                      >
                        Copy Installer
                      </Button>
                    </div>
                  ),
                },
              ]}
              data={tokens}
              loading={loading}
              emptyMessage="No enrollment tokens are available."
              pagination={{
                page,
                pageSize,
                total,
                onPageChange: setPage,
              }}
            />
          ) : (
            <EmptyState
              icon={<KeyRound className="h-8 w-8" />}
              title="No tokens"
              description="Create an enrollment token to onboard a new managed device."
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="border-slate-700 bg-slate-800 text-slate-300">
          <DialogHeader>
            <DialogTitle className="text-white">Create Token</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={createToken}>
            <input
              type="number"
              min="1"
              value={form.max_uses}
              onChange={(event) => setForm((current) => ({ ...current, max_uses: event.target.value }))}
              placeholder="Device limit"
              className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={form.expires_hours}
              onChange={(event) => setForm((current) => ({ ...current, expires_hours: event.target.value }))}
              className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-white outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="24">24h</option>
              <option value="48">48h</option>
              <option value="168">7d</option>
            </select>
            <Button className="w-full bg-blue-600 text-white hover:bg-blue-700" disabled={saving} type="submit">
              {saving ? 'Creating...' : 'Create Token'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showInstaller} onOpenChange={setShowInstaller}>
        <DialogContent className="border-slate-700 bg-slate-800 text-slate-300">
          <DialogHeader>
            <DialogTitle className="text-white">Installer Commands</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="mac" className="space-y-4">
            <TabsList className="bg-slate-900">
              <TabsTrigger className="data-[state=active]:bg-slate-700 data-[state=active]:text-white" value="mac">
                macOS / Linux
              </TabsTrigger>
              <TabsTrigger className="data-[state=active]:bg-slate-700 data-[state=active]:text-white" value="windows">
                Windows
              </TabsTrigger>
            </TabsList>
            <TabsContent value="mac">
              <div className="space-y-3">
                <pre className="overflow-auto rounded-lg border border-slate-700 bg-slate-900 p-4 text-xs text-slate-200">
                  {installerCommands.mac}
                </pre>
                <Button
                  className="w-full bg-blue-600 text-white hover:bg-blue-700"
                  onClick={() => navigator.clipboard.writeText(installerCommands.mac)}
                >
                  <Copy className="h-4 w-4" />
                  Copy macOS Installer
                </Button>
              </div>
            </TabsContent>
            <TabsContent value="windows">
              <div className="space-y-3">
                <pre className="overflow-auto rounded-lg border border-slate-700 bg-slate-900 p-4 text-xs text-slate-200">
                  {installerCommands.windows}
                </pre>
                <Button
                  className="w-full bg-blue-600 text-white hover:bg-blue-700"
                  onClick={() => navigator.clipboard.writeText(installerCommands.windows)}
                >
                  <Copy className="h-4 w-4" />
                  Copy PowerShell Installer
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
